import { useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { useCreateVersion } from '@/hooks/usePlanDetail'

interface SaveVersionModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  planId: string
}

export function SaveVersionModal({
  open,
  onOpenChange,
  planId,
}: SaveVersionModalProps) {
  const [versionName, setVersionName] = useState('')
  const [note, setNote] = useState('')
  const createVersion = useCreateVersion(planId)

  const handleSave = () => {
    createVersion.mutate(
      { alias: versionName, notes: note || null },
      { onSuccess: () => handleClose() }
    )
  }

  const handleClose = () => {
    setVersionName('')
    setNote('')
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle>Save New Version</DialogTitle>
          <DialogDescription>
            Create a snapshot of the current plan state.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label htmlFor="version-name">Version Name</Label>
            <Input
              id="version-name"
              placeholder="e.g. Q1 Final Draft"
              value={versionName}
              onChange={e => setVersionName(e.target.value)}
              maxLength={50}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="version-note">Add a note (optional)</Label>
            <Textarea
              id="version-note"
              placeholder="Describe what changed in this version..."
              value={note}
              onChange={e => setNote(e.target.value)}
              rows={3}
            />
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={createVersion.isPending}
            className="cursor-pointer"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={!versionName.trim() || createVersion.isPending}
            className="cursor-pointer"
          >
            {createVersion.isPending ? 'Saving...' : 'Save Version'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
